import styled from 'styled-components';
import { tokens } from '../../utils';

/* Help-FAB bottom clearance. The Help Assistant FAB is fixed to the bottom-right
   corner (src/components/HelpAssistant: FAB_SIZE 56 + corner inset 24 at >=sm /
   16 below 600px → top edge reaches 80px / 72px up from the viewport bottom).
   MEASURED @390 on web-stg 2026-08-15 (no modal open — distinct from
   BUG-explore-398d6d, which only hides the FAB while a MODAL is open): the FAB
   floated over the LAST notification row's thumbnail + unread dot. Reserve
   scroll clearance below the list so the last row always clears the FAB. */
export const FAB_CLEARANCE_PX = 104; // 80px desktop reach + 24px gap (covers mobile)

// vy-0914-nc-all-help-fab-covers-notification-row-control-390-fix-0925: a
// trailing `padding-bottom` inside the scroll container only reserves space
// AFTER the last row — it protects the scrolled-to-end rest position (verified
// still holding: bundle main.7adbc99c.js, web-dev.iyuga.net, 390x844, last row
// box {x:11.7,y:504,w:366.6,h:132} vs FAB {x:318,y:772,w:56,h:56} -> no
// intersection) but does nothing for every OTHER scroll position, including
// the very first thing a user sees on page load. MEASURED live (same bundle,
// same viewport, UNSCROLLED): the row sitting at the bottom edge of the
// initial viewport ("5 days ago" row, box {x:11.7,y:792,w:366.6,h:132}) DID
// intersect the FAB ({x:318,y:772,w:56,h:56}) — the row's per-item kebab
// control (NotificationItem.styled.js KebabButton, absolute top:6/right:8,
// visible+tappable on touch per fix-resp0919-touch-targets) landed directly
// under the FAB's higher z-index (HELP_FAB_Z_INDEX, HelpAssistant/index.jsx),
// the same G-139 "visible but not clickable" class fabPosition.js documents
// for the consent-banner/rail/header obstructions.
//
// A scroll container's height is its own permanently-visible window — nothing
// can ever render past the container's bottom edge regardless of scroll
// offset — where trailing padding only ever reaches that same offset once
// scrolled ALL THE WAY down. Reserving the clearance as HEIGHT instead of
// trailing padding makes the FAB's corner sit over empty space (whatever is
// behind this unstyled container, not list content) at every scroll position,
// not just the two rest points.
//
// Scroll container for the whole notifications page. N7.16 (cross-browser LAW,
// iOS): 100dvh (not 100vh) so the scroll region isn't stuck at the pre-scroll
// height on iOS Safari (URL-bar jump).
export const ScrollPage = styled.div`
  height: calc(100dvh - ${FAB_CLEARANCE_PX}px - env(safe-area-inset-bottom, 0px));
  overflow-y: auto;
`;

// Container for the notifications, centered in the viewport
export const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center; /* Centers the container */
  width: 60vw;
  background-color: white;
  margin-bottom: 50px;

  /* mobile-wrap-390 (measured live web-stg 2026-07-30, /notifications @390px):
     60vw with no mobile breakpoint pinned this whole page to 234px on a phone
     viewport. NotificationItem's row already reserves fixed width for the
     avatar (50px), wheel thumbnail (60px) and row padding (40px), so at 234px
     the text column (which DOES have flex:1/min-width:0 -- see
     NotificationItem.styled.js LeftSection/TextContainer) was squeezed down to
     ~34px and wrapped every word into single-character fragments. Widening
     the page container below the tablet breakpoint gives the row real room;
     desktop (60vw) is unchanged. */
  @media (max-width: 768px) {
    width: 94vw;
  }
`;

// Wrapper for each notification, with space between text and the notification box
export const NotificationWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 100%; /* Full width of the container */
`;

export const ModalButtonContainer = styled.div`
  display: flex;
  gap: 10px;
  margin: 20px 0 20px 0;
  width: 60vw;
`;

// Text above each notification, can be styled as needed
export const Text = styled.div`
  font-size: 16px;
  font-weight: bold;
  color: white;
  margin-bottom: 8px;
`;

// Top bar for notifications header
export const TopBar = styled.div`
  display: flex;
  justify-content: space-between; /* Space between left and right parts */
  padding: 16px;
  background-color: white;
  width: 60vw; /* Same width as the container */
  box-shadow: 0px 4px 8px rgba(0, 0, 0, 0.2);
  border-top-left-radius: 8px;
  border-top-right-radius: 8px;

  position: relative;
  margin-top: 20px;

  /* mobile-wrap-390: keep in lockstep with Container's mobile width above so
     the header bar and the row list stay the same width on a phone viewport. */
  @media (max-width: 768px) {
    width: 94vw;
  }
`;

// Notifications count circle
export const NotificationCount = styled.div`
  /* DL-004 (ISP-4018): white count digits -> on-fill token (#8F38EF, AA 5.28:1). */
  background-color: ${tokens.brand.primaryActive};
  color: white;
  border-radius: 50%;
  width: 24px;
  height: 24px;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 12px;
  font-weight: 500;
`;

// Container for the mark as read and options
export const LeftBar = styled.div`
  display: flex;
  gap: 16px;
  align-items: center;
`;

// Icon styles for the three dots and mark as read button
export const IconButton = styled.div`
  cursor: pointer;
  font-size: 18px;
  color: gray;

  &:hover {
    color: black;
  }
`;
